/**
 * Employee Program Consent Page
 *
 * Displays pending program offers and allows employees to accept/decline
 * participation in employer support programs with proper consent tracking.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FileCheck,
  FileX,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronRight,
  Shield,
  Calendar,
  Building2,
  Loader2,
  Info,
  History,
} from 'lucide-react';
import { getMemberToken } from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';

interface ProgramOffer {
  consent_id: number;
  program_id: number;
  program_name: string;
  program_description: string | null;
  employer_name: string;
  status: string;
  offered_at: string;
  expires_at: string | null;
  consent_text: string | null;
  requires_signature: boolean;
}

interface ConsentStatus {
  consent_id: number;
  program_id: number;
  program_name: string;
  status: string;
  offered_at: string;
  responded_at: string | null;
  accepted_at: string | null;
  signature_method: string | null;
  can_revoke: boolean;
}

export default function ConsentPage() {
  const [pendingOffers, setPendingOffers] = useState<ProgramOffer[]>([]);
  const [consentHistory, setConsentHistory] = useState<ConsentStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOffer, setSelectedOffer] = useState<ProgramOffer | null>(null);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [signatureText, setSignatureText] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [acknowledgeTerms, setAcknowledgeTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'pending' | 'history'>('pending');

  const token = getMemberToken();

  // Fetch pending offers and consent history
  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      try {
        // Fetch pending offers
        const offersResponse = await fetch(
          getWeaveinApiUrl('/api/employee-consent/offers/pending'),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
              'Content-Type': 'application/json',
            },
          }
        );

        if (offersResponse.ok) {
          const offersData = await offersResponse.json();
          setPendingOffers(offersData);
        }

        // Fetch consent history
        const historyResponse = await fetch(
          getWeaveinApiUrl('/api/employee-consent/status'),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
              'Content-Type': 'application/json',
            },
          }
        );

        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          setConsentHistory(historyData);
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching consent data:', err);
        setError('Failed to load consent information');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  // Handle accept consent
  const handleAccept = async () => {
    if (!selectedOffer || !signatureText || !acknowledgeTerms) return;

    setSubmitting(true);
    try {
      const response = await fetch(
        getWeaveinApiUrl('/api/employee-consent/accept'),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            consent_id: selectedOffer.consent_id,
            signature_text: signatureText,
            acknowledge_terms: true,
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();

        // Remove from pending offers
        setPendingOffers(prev =>
          prev.filter(o => o.consent_id !== selectedOffer.consent_id)
        );

        // Add to history
        setConsentHistory(prev => [{
          consent_id: selectedOffer.consent_id,
          program_id: selectedOffer.program_id,
          program_name: selectedOffer.program_name,
          status: 'accepted',
          offered_at: selectedOffer.offered_at,
          responded_at: new Date().toISOString(),
          accepted_at: new Date().toISOString(),
          signature_method: 'electronic',
          can_revoke: true,
        }, ...prev]);

        // Close modal and reset
        setShowAcceptModal(false);
        setSelectedOffer(null);
        setSignatureText('');
        setAcknowledgeTerms(false);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to accept offer');
      }
    } catch (err) {
      console.error('Error accepting offer:', err);
      setError('Failed to accept offer');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle decline consent
  const handleDecline = async () => {
    if (!selectedOffer) return;

    setSubmitting(true);
    try {
      const response = await fetch(
        getWeaveinApiUrl('/api/employee-consent/decline'),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            consent_id: selectedOffer.consent_id,
            reason: declineReason || undefined,
          }),
        }
      );

      if (response.ok) {
        // Remove from pending offers
        setPendingOffers(prev =>
          prev.filter(o => o.consent_id !== selectedOffer.consent_id)
        );

        // Add to history
        setConsentHistory(prev => [{
          consent_id: selectedOffer.consent_id,
          program_id: selectedOffer.program_id,
          program_name: selectedOffer.program_name,
          status: 'declined',
          offered_at: selectedOffer.offered_at,
          responded_at: new Date().toISOString(),
          accepted_at: null,
          signature_method: null,
          can_revoke: false,
        }, ...prev]);

        // Close modal and reset
        setShowDeclineModal(false);
        setSelectedOffer(null);
        setDeclineReason('');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to decline offer');
      }
    } catch (err) {
      console.error('Error declining offer:', err);
      setError('Failed to decline offer');
    } finally {
      setSubmitting(false);
    }
  };

  // Get status badge color
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'declined':
        return 'bg-red-100 text-red-800';
      case 'revoked':
        return 'bg-gray-100 text-gray-800';
      case 'expired':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'accepted':
        return <CheckCircle2 className="w-4 h-4" />;
      case 'declined':
        return <XCircle className="w-4 h-4" />;
      case 'revoked':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Program Consent</h1>
          <p className="text-gray-600 mt-1">
            Review and respond to employer support program offers
          </p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <Shield className="w-4 h-4" />
          <span>Your consent is required to participate</span>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('pending')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'pending'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center space-x-2">
              <FileCheck className="w-4 h-4" />
              <span>Pending Offers</span>
              {pendingOffers.length > 0 && (
                <span className="ml-2 bg-indigo-100 text-indigo-600 py-0.5 px-2 rounded-full text-xs">
                  {pendingOffers.length}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'history'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center space-x-2">
              <History className="w-4 h-4" />
              <span>Consent History</span>
            </span>
          </button>
        </nav>
      </div>

      {/* Pending Offers Tab */}
      {activeTab === 'pending' && (
        <div className="space-y-4">
          {pendingOffers.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">All Caught Up!</h3>
              <p className="text-gray-600">You have no pending program offers at this time.</p>
            </div>
          ) : (
            pendingOffers.map((offer) => (
              <div
                key={offer.consent_id}
                className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {offer.program_name}
                      </h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(offer.status)}`}>
                        {offer.status}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-3">
                      {offer.program_description || 'No description available'}
                    </p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center space-x-1">
                        <Building2 className="w-4 h-4" />
                        <span>{offer.employer_name}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>Offered {new Date(offer.offered_at).toLocaleDateString()}</span>
                      </span>
                      {offer.expires_at && (
                        <span className="flex items-center space-x-1 text-orange-600">
                          <Clock className="w-4 h-4" />
                          <span>Expires {new Date(offer.expires_at).toLocaleDateString()}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 ml-4">
                    <button
                      onClick={() => {
                        setSelectedOffer(offer);
                        setShowDeclineModal(true);
                      }}
                      className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                    >
                      Decline
                    </button>
                    <button
                      onClick={() => {
                        setSelectedOffer(offer);
                        setShowAcceptModal(true);
                      }}
                      className="px-4 py-2 text-white bg-v2accent hover:bg-v2accent/90 rounded-lg font-medium transition-colors flex items-center space-x-2"
                    >
                      <span>Review & Accept</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Consent History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          {consentHistory.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No History Yet</h3>
              <p className="text-gray-600">Your consent decisions will appear here.</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Program
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Offered
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Responded
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {consentHistory.map((item) => (
                    <tr key={item.consent_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {item.program_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(item.status)}`}>
                          {getStatusIcon(item.status)}
                          <span className="capitalize">{item.status}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(item.offered_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.responded_at
                          ? new Date(item.responded_at).toLocaleDateString()
                          : '-'
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {item.can_revoke && (
                          <button className="text-red-600 hover:text-red-800 font-medium">
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Accept Modal */}
      {showAcceptModal && selectedOffer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                Accept Program Enrollment
              </h2>
              <p className="text-gray-600 mt-1">
                {selectedOffer.program_name}
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* Consent Text */}
              {selectedOffer.consent_text ? (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="font-medium text-gray-900 mb-2 flex items-center space-x-2">
                    <Info className="w-4 h-4" />
                    <span>Program Terms</span>
                  </h3>
                  <div className="text-sm text-gray-700 prose max-w-none">
                    {selectedOffer.consent_text}
                  </div>
                </div>
              ) : (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <p className="text-sm text-blue-800">
                    By accepting, you agree to participate in the {selectedOffer.program_name}
                    program offered by {selectedOffer.employer_name}. Your participation data
                    will be used to provide you with personalized support services.
                  </p>
                </div>
              )}

              {/* Signature */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Electronic Signature
                </label>
                <input
                  type="text"
                  placeholder="Type your full name"
                  value={signatureText}
                  onChange={(e) => setSignatureText(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Your typed name serves as your electronic signature
                </p>
              </div>

              {/* Acknowledgment */}
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acknowledgeTerms}
                  onChange={(e) => setAcknowledgeTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">
                  I acknowledge that I have read and understand the program terms,
                  and I voluntarily agree to participate. I understand I can revoke
                  my consent at any time.
                </span>
              </label>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAcceptModal(false);
                  setSelectedOffer(null);
                  setSignatureText('');
                  setAcknowledgeTerms(false);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={handleAccept}
                disabled={!signatureText || !acknowledgeTerms || submitting}
                className="px-6 py-2 text-white bg-v2accent hover:bg-v2accent/90 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Accept & Enroll</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Decline Modal */}
      {showDeclineModal && selectedOffer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                Decline Program Offer
              </h2>
              <p className="text-gray-600 mt-1">
                {selectedOffer.program_name}
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <p className="text-sm text-yellow-800">
                  Are you sure you want to decline this program offer?
                  You may be able to request enrollment again later.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for declining (optional)
                </label>
                <textarea
                  placeholder="Let us know why you're declining..."
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeclineModal(false);
                  setSelectedOffer(null);
                  setDeclineReason('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={handleDecline}
                disabled={submitting}
                className="px-6 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg font-medium disabled:opacity-50 flex items-center space-x-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4" />
                    <span>Decline Offer</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
